import React from 'react';
import { I18n } from 'react-redux-i18n';
import { Modal, ModalBody, ModalHeader, ModalTitle } from '@common/components/modal';
import { FormControl } from '@common/components/form-control';
import { getTestAttributes } from '@common/utils/tests';
import { Flex } from '@common/components/Flex';
import { Button } from '@common/components/Button';
import { MODULE_NAME } from '@modules/pedigrees/constants/shared';
import { HttpErrorDto } from '@common/dto/http';

import './individual-renaming.styles.scss';
import {sanitize} from "@common/utils/sanitize";

export type IndividualsRenamingProps = {
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
  showErrorMessage: (msg: string) => void;
  defaultValue: string;
};

export const IndividualRenaming: React.FC<IndividualsRenamingProps> = (props) => {
  const localModuleName = `${MODULE_NAME}-renaming-individual-modal`;
  const { onClose, onSubmit, showErrorMessage, defaultValue } = props;
  const [value, setValue] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  }, []);

  const handleSubmit = React.useCallback(async () => {
    setSubmitting(true);

    try {
      await onSubmit(sanitize(value.trim()));
    } catch (err) {
      setSubmitting(false);
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to rename individual'));
      console.error(err);
    }
  }, [value, onSubmit, showErrorMessage]);

  return (
    <Modal
      show
      animation={false}
      bsSize="medium"
      backdrop="static"
      bsClass="bootstrap modal"
      className="PedigreesIndividualRenaming"
      onEscapeKeyDown={onClose}
      onHide={onClose}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle className="PedigreesIndividualRenaming__title" {...getTestAttributes(localModuleName, 'header', 'title')} title={defaultValue}>
          {I18n.t('Rename Individual')} - {defaultValue}
        </ModalTitle>
      </ModalHeader>

      <ModalBody>
        <Flex justify="space-between" alignItems="center">
          <FormControl
            placeholder={I18n.t('Type new individual name')}
            className="PedigreesIndividualRenaming__name-input"
            {...getTestAttributes({ id: 'tx-rename-ind-ind-name' })}
            onChange={handleChange}
          />
          <Button 
            className="PedigreesIndividualRenaming__submit-btn"
            disabled={!value.trim().length || submitting}
            {...getTestAttributes({ id: 'tx-rename-ind-save' })}
            onClick={handleSubmit}
          >
            {submitting ? I18n.t('Loading') : I18n.t('Rename')}
          </Button>
        </Flex>
      </ModalBody>
    </Modal>
  );
};